import { useState, useEffect, useCallback, useRef } from 'react';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { readerApi } from '../services/api';
import useChapterLogger from '../hooks/useChapterLogger';
import LogMoment from '../components/log/LogMoment';
import { chapterApi, esvApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ChapterSelector from '../components/reading/ChapterSelector';
import ChapterDisplay from '../components/reading/ChapterDisplay';
import ChapterNavigation from '../components/reading/ChapterNavigation';
import './ReadingPage.css';

const ReadingPage = () => {
  const textRequest = useRef(0);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapterText, setChapterText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [textLoading, setTextLoading] = useState(false);
  const [error, setError] = useState(null);
  const [textFormat, setTextFormat] = useState('text'); // 'text' or 'html'
  const { user } = useAuth();
  const {
    reader, logChapter, wasLogged, logging, error: loggingError,
    moment, momentLabel, clearMoment, undo, saveReflection, hasReports
  } = useChapterLogger({ userId: user?.id });
  const [esvOptions, setEsvOptions] = useState({
    'include-verse-numbers': true,
    'include-headings': false,
    'include-footnotes': false
  });

  // Load all chapters on component mount
  useEffect(() => {
    loadChapters();
  }, []);

  const loadChapters = async () => {
    try {
      setLoading(true);
      
      // Fetch the complete catalog using the API's supported page size.
      const response = await chapterApi.getAllChapters();
      
      // The API returns { chapters: [...], pagination: {...} }
      const chaptersData = response.data.chapters;
      
      if (!Array.isArray(chaptersData)) {
        throw new Error('Invalid chapters data received from API');
      }
      
      setChapters(chaptersData);

      // WS5: a claim deep-links here with ?chapter=id. Prefer that over
      // "next chapter" so "Read Nahum 2" actually opens Nahum 2.
      const requestedId = parseInt(new URLSearchParams(window.location.search).get('chapter'), 10);
      let startingChapter = Number.isInteger(requestedId)
        ? chaptersData.find(ch => ch.id === requestedId) || null
        : null;

      // WS7: open where the reader left off, not at Genesis 1 every time.
      if (!startingChapter) {
        try {
          const readersResponse = await readerApi.getReaders();
          const list = readersResponse.data.readers || [];
          const primary = list.find(r => r.isPrimary) || list[0];
          if (primary) {
            const next = await readerApi.getNextChapter(primary.id);
            const nextId = next.data.nextChapter?.chapterId;
            startingChapter = chaptersData.find(ch => ch.id === nextId) || null;
          }
        } catch (nextError) {
          // Not critical — fall back to Genesis 1.
          console.warn('Could not resolve next chapter, starting at Genesis 1:', nextError);
        }
      }

      const firstChapter = startingChapter
        || chaptersData.find(ch => ch.id === 1)
        || chaptersData[0];
      if (firstChapter) {
        setCurrentChapter(firstChapter);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to load chapters:', err);
      setError('Failed to load Bible chapters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadChapterText = useCallback(async () => {
    if (!currentChapter) return;
    const request = ++textRequest.current;

    try {
      setTextLoading(true);
      
      const apiMethod = textFormat === 'html' ? esvApi.getChapterHTML : esvApi.getChapterText;
      const response = await apiMethod(currentChapter.id, esvOptions);
      
      if (request !== textRequest.current) return;
      setChapterText(response.data.data);
      setError(null);
    } catch (err) {
      if (request !== textRequest.current) return;
      console.error('Failed to load chapter text:', err);
      
      let errorMessage = 'Failed to load chapter text. Please try again.';
      
      if (err.response?.data?.errorType) {
        errorMessage = err.response.data.error || errorMessage;
        
        // Add retry information for rate limiting
        if (err.response.data.errorType === 'rate_limit' && err.response.data.retryAfter) {
          errorMessage += ` (Retry in ${err.response.data.retryAfter} seconds)`;
        }
      } else if (err.response?.status === 429) {
        errorMessage = 'ESV API rate limit exceeded. Please try again in a few minutes.';
      } else if (err.response?.status === 408) {
        errorMessage = 'Request timed out. Please check your internet connection.';
      }
      
      setError(errorMessage);
      setChapterText(null);
    } finally {
      if (request === textRequest.current) setTextLoading(false);
    }
  }, [currentChapter, textFormat, esvOptions]);

  useEffect(() => {
    loadChapterText();
    return () => { textRequest.current += 1; };
  }, [loadChapterText]);

  const handleChapterChange = (chapterId) => {
    const chapter = chapters.find(ch => ch.id === parseInt(chapterId));
    if (chapter) {
      setCurrentChapter(chapter);
    }
  };

  const navigateToChapter = (direction) => {
    if (!currentChapter || !chapters.length) return;

    const currentIndex = chapters.findIndex(ch => ch.id === currentChapter.id);
    let newIndex;

    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : chapters.length - 1;
    } else {
      newIndex = currentIndex < chapters.length - 1 ? currentIndex + 1 : 0;
    }

    setCurrentChapter(chapters[newIndex]);
  };

  // WS7: finishing the audio, or tapping Mark as read, both log the chapter
  // and then move on to the next one.
  const advance = useCallback((finished) => {
    setCurrentChapter(prev => {
      if (!prev || !chapters.length || prev.id !== finished?.id) return prev;
      const index = chapters.findIndex(ch => ch.id === prev.id);
      return index >= 0 && index < chapters.length - 1 ? chapters[index + 1] : prev;
    });
  }, [chapters]);

  const handleChapterFinished = useCallback(async (finished) => {
    const chapter = finished || currentChapter;
    const logged = await logChapter(chapter);
    if (logged) advance(chapter);
  }, [logChapter, currentChapter, advance]);

  const handleMarkAsRead = useCallback(async () => {
    const logged = await logChapter(currentChapter);
    if (logged) advance(currentChapter);
  }, [logChapter, currentChapter, advance]);

  const handleFormatChange = (format) => {
    setTextFormat(format);
  };

  const handleOptionsChange = (newOptions) => {
    setEsvOptions(prev => ({ ...prev, ...newOptions }));
  };

  const getBookChapters = () => {
    if (!currentChapter || !chapters.length) return [];
    return chapters.filter(ch => ch.book === currentChapter.book);
  };

  const getUniqueBooks = () => {
    if (!chapters.length) return [];
    const books = [...new Set(chapters.map(ch => ch.book))];
    return books;
  };

  if (loading) {
    return (
      <div className="reading-page loading">
        <LoadingSpinner size="large" message="Loading Bible chapters..." />
      </div>
    );
  }

  if (error && !chapters.length) {
    return (
      <div className="reading-page error">
        <div className="error-message">
          <h2>Unable to load the Bible</h2>
          <p>{error}</p>
          <button onClick={loadChapters} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reading-page">
      <div className="reading-header">
        <h1>Read</h1>
        <p>Read through the Bible one chapter at a time</p>
      </div>

      <div className="reading-controls">
        <ChapterSelector
          chapters={chapters}
          currentChapter={currentChapter}
          onChapterChange={handleChapterChange}
          books={getUniqueBooks()}
          bookChapters={getBookChapters()}
        />
        
        <details className="reading-preferences"><summary>Reading appearance</summary><div className="reading-options">
          <div className="format-selector">
            <label>
              <input
                type="radio"
                name="format"
                value="text"
                checked={textFormat === 'text'}
                onChange={(e) => handleFormatChange(e.target.value)}
              />
              Plain text
            </label>
            <label>
              <input
                type="radio"
                name="format"
                value="html"
                checked={textFormat === 'html'}
                onChange={(e) => handleFormatChange(e.target.value)}
              />
              Formatted
            </label>
          </div>

          <div className="text-options">
            <label>
              <input
                type="checkbox"
                checked={esvOptions['include-verse-numbers']}
                onChange={(e) => handleOptionsChange({ 'include-verse-numbers': e.target.checked })}
              />
              Verse numbers
            </label>
            <label>
              <input
                type="checkbox"
                checked={esvOptions['include-headings']}
                onChange={(e) => handleOptionsChange({ 'include-headings': e.target.checked })}
              />
              Headings
            </label>
            <label>
              <input
                type="checkbox"
                checked={esvOptions['include-footnotes']}
                onChange={(e) => handleOptionsChange({ 'include-footnotes': e.target.checked })}
              />
              Footnotes
            </label>
          </div>
        </div>
      </details></div>

      <ChapterNavigation
        onNavigate={navigateToChapter}
        currentChapter={currentChapter}
        chapters={chapters}
      />

      <div className="reading-content">
        {textLoading ? (
          <div className="text-loading">
            <LoadingSpinner message="Loading chapter text..." />
          </div>
        ) : error ? (
          <div className="text-error">
            <p>{error}</p>
            <button onClick={loadChapterText} className="retry-button">
              Retry
            </button>
          </div>
        ) : (
          <ChapterDisplay
            key={currentChapter?.id}
            chapter={currentChapter}
            onChapterFinished={handleChapterFinished}
            chapterText={chapterText}
            format={textFormat}
          />
        )}
      </div>

      {loggingError && <p className="text-error" role="alert">{loggingError}</p>}

      {/* WS7: log what you just read without leaving the page */}
      {currentChapter && reader && (
        <div className="mark-as-read-bar">
          <div className="mark-as-read-copy">
            <strong>Finished {currentChapter.book} {currentChapter.chapter}?</strong>
            <span>Logging it for {reader.name}.</span>
          </div>
          <button
            type="button"
            className={`mark-as-read-button ${wasLogged(currentChapter) ? 'done' : ''}`}
            onClick={handleMarkAsRead}
            disabled={logging || wasLogged(currentChapter)}
          >
            <IoCheckmarkCircleOutline size={18} aria-hidden="true" />
            {wasLogged(currentChapter) ? 'Logged' : logging ? 'Logging…' : 'Mark as read'}
          </button>
        </div>
      )}

      <ChapterNavigation
        onNavigate={navigateToChapter}
        currentChapter={currentChapter}
        chapters={chapters}
        showBottom={true}
      />

      {moment && (
        <LogMoment
          moment={moment}
          chapterLabel={momentLabel}
          onClose={clearMoment}
          onUndo={undo}
          onSaveReflection={hasReports ? saveReflection : undefined}
        />
      )}
    </div>
  );
};

export default ReadingPage;
